import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'utils/api/axios'
import AxiosUtils from 'utils/api/axiosUtils'
import Modal from '@airbus/components-react/components/Modal'
import { Message, MessageSeverity } from 'constants/message'
import { HTTP_CODES } from 'constants/constants'
import { TableCell, Table, TableHead, TableBody, TableRow, Container } from '@airbus/components-react/components'
import GoToTop from 'components/goToTop'
import URL_APIS from 'utils/api/urlApis'
import tools from 'utils/helpers'
import PageHeader from 'components/pageHeader'
import { SnSearch } from 'types/pn'
import MessageContext from 'context/MessageContext'
import AuthContext from 'context/AuthContext'
import LightDarkContext from 'context/LightDarkModeContext'

const ID_SCROLL_COMPONENT_FOR_GOTOTOP = 'modalPnGoToTop'

type Props = {
  formTitle: string
  isOpened: boolean
  pn: string
  avnumap?: string
  onCloseModal: () => void
  onSnSelected: () => void
}

const urlSn = URL_APIS.URL_COMMON_SN_SEARCH_FOR_PN_GET_PARAMETERIZED

const styleModal = {
  position: 'absolute',
  transform: 'translate(50%, 50%)',
  width: '50%',
  height: '50%',
  border: '2px solid #000',
  boxShadow: 24,
  overflowY: 'scroll',
  scrollbarWidth: 'none',
}

const ModalSn = ({ formTitle, isOpened, pn, avnumap, onCloseModal, onSnSelected }: Props) => {
  const { t } = useTranslation()

  const [data, setData] = useState([])

  const [isLoading, setIsLoading] = useState(false)
  const { handleDisplayMessage } = useContext(MessageContext)
  const { handleRequestLogin } = useContext(AuthContext)

  const loadData = async () => {
    try {
      console.debug(
        'Page <' +
          formTitle +
          '> :: Recherche <SN> :: called URL = ' +
          encodeURI(tools.dformat(urlSn, pn, avnumap ? avnumap : '')) +
          "'",
      )
      setIsLoading(true)

      const headerCompleted = await AxiosUtils.getHeader(AxiosUtils.HEADERS.HEADER_READ)
      const response = await axios.get(encodeURI(tools.dformat(urlSn, pn, avnumap ? avnumap : '')), {
        headers: headerCompleted,
        withCredentials: false,
      })

      // Init widget locally
      console.debug('data retrieved <' + formTitle + '> :: Recherche <SN>')
      setData(response?.data)
    } catch (err: any) {
      console.debug('ERR <' + t('9500054') + '>: ' + err)
      if (err?.response?.status === HTTP_CODES.HTTP_CODE_SESSION_EXPIRED) {
        handleDisplayMessage(new Message(MessageSeverity.WARNING, t('9500054'), null, t('sessionExpired')))
        handleRequestLogin()
      } else if (err.response.status === HTTP_CODES.HTTP_CODE_NOT_FOUND) {
        handleDisplayMessage(new Message(MessageSeverity.ERROR, t('9500054'), null, t('9500173')))
      } else {
        handleDisplayMessage(
          new Message(
            MessageSeverity.ERROR,
            t('9500054'),
            err.response.data?.amasisCode,
            err.response.data?.description
              ? err.response.data?.description
              : err.response.data.title
                ? err.response.data.title
                : err.response.data.message
                  ? err.response.data.message
                  : err.response.data.error,
          ),
        )
      }
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (pn) {
      loadData()
    }

    return () => setData([])
  }, [pn])

  const { getBackgroundColor } = useContext(LightDarkContext)

  return (
    <Modal
      open={isOpened}
      onClose={onCloseModal}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <div style={{ ...styleModal, backgroundColor: getBackgroundColor() }} id={ID_SCROLL_COMPONENT_FOR_GOTOTOP}>
        <>
          <PageHeader
            pageTitle={t('modal_search_sn')}
            modalEmbedded={true}
            modalOnClose={onCloseModal}
            style={{ top: 0 }}
            localLoading={isLoading}
          />

          <Container style={{ flex: 1 }} fixed>
            <div style={{ padding: '1em' }}>
              {data && data.length > 0 ? (
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('9500048')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.map((row: SnSearch, i: number) => (
                      <TableRow key={'snSearch_' + i} hover={true} onClick={() => onSnSelected(row)}>
                        <TableCell>{row?.sn}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div>{t('9500055')}</div>
              )}
            </div>
          </Container>
          <GoToTop idComponentForScroll={ID_SCROLL_COMPONENT_FOR_GOTOTOP} />
        </>
      </div>
    </Modal>
  )
}

export default ModalSn
